import { useEffect, useMemo, useState, useContext } from 'react';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridDivider from '../../Shared/Page/PageGridDivider'
import SearchTermFilter from '../../../components/Input/SearchTermFilter';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Table from '../../../components/Table/Table';
import { useRemote, minutesIntoReadableFormat } from '../../../Utils/Utils';
import { BannerContext } from "../../../InternalApiApp";
import TickerSectorItem from '../../../components/TickerSectorItem';
import FlexWrapper from '../../FlexWrapper'
import LoadingIcon from '../../Shared/LoadingIcon'
import Tooltip from '../../../components/popups/Tooltip';
import { ChevronUp, ChevronDown, Clock, Download, Eye } from 'react-feather';
import { useHistory } from 'react-router';
import { PAGES, ROUTE_PATHS } from '../../../InternalApiApp';
import { HeaderTickerDisplay, analyticsExportFilenameDateless } from '../AnalyticsUtils';
import AnalyticsSourceSelector from "../AnalyticsSourceSelector";
import AnalyticsExportButton from '../Components/AnalyticsExportButton';


const AnalyticsPortfoliosList = () => {
	const { setBannerBreadCrumbs, setBannerAdditionalContent, setBacNoDivider, bannerAdditionalInfo } = useContext(BannerContext);

	const remote = useRemote();
	const history = useHistory();
	const [hasSearchTerm, setHasSearchTerm] = useState("")
	const [filteredData, setFilteredData] = useState([])
	const [loading, setLoading] = useState(true)
	const [aggregates, setAggregates] = useState([]);
	const [sortingColumn, setSortingColumn] = useState({ column: "Code", ascending: true })

	useEffect(() => {
		setBannerBreadCrumbs({ tab: 'Portfolios' })
		setBacNoDivider(true)
		setBannerAdditionalContent(<AnalyticsSourceSelector />)

		return () => {
			setBacNoDivider(false)
			setBannerAdditionalContent(null)
		}
	}, [])

	// get portfolios
	useEffect(() => {
		const products = bannerAdditionalInfo?.platforms.map(platform =>
			platform.products.map(p => p.selected ? `product=${p.id}` : null)
				.filter(e => e !== null)
				.join("&")
		)


		Promise.all([
			remote.get(`/funds/portfolio/static-data?key=pm_logo&key=cfs_logo&key=portfolio_manager_name${products ? "&" + products : ""}`),
			remote.get(`/analytics/aggregates/funds${products ? "?" + products : ""}`)
		]).then(([staticResponse, analyticsResponse]) => {
			const fundsObj = staticResponse.reduce((a, c) => ({ ...a, [c.fundNumber]: c }), {})
			const analyticsWithManagerName = analyticsResponse.map(a => {
				const matchingFund = fundsObj[a.number]
				if (matchingFund !== undefined) {
					const pmLogo = matchingFund.keyValues.find(k => k.key === "pm_logo" && k.value.startsWith("url:"))
					const cfsLogo = matchingFund.keyValues.find(k => k.key === "cfs_logo" && k.value.startsWith("url:"))
					const logo = pmLogo && pmLogo.value.startsWith("url:") ? pmLogo.value.replace("url:", "") : null
					const manager = matchingFund.keyValues.find(k => k.key === "portfolio_manager_name")?.value || ""
					return { ...a, manager, logo: logo ? logo : cfsLogo && cfsLogo.value.startsWith("url:") ? cfsLogo.value.replace("url:", "") : null }
				} else {
					return { ...a, manager: "", logo: null }
				}
			})
			setAggregates(analyticsWithManagerName)
			setLoading(false)
		});

	}, [remote, setBannerBreadCrumbs, bannerAdditionalInfo]);

	// Clear Sorting
	useEffect(() => {
		setSortingColumn({ column: "Code", ascending: true })
	}, [aggregates, filteredData])

	const toggleSort = (column) => {
		if (column === null) {
			setSortingColumn({ column: "Code", ascending: true })
			return
		}

		const ascending = sortingColumn.column === column ? !sortingColumn.ascending : true
		setSortingColumn({ column: column, ascending: ascending })
	}

	const sortTable = (dataToSort) => {
		if (sortingColumn.column === null) {
			return dataToSort
		}
		if (!dataToSort) {
			return dataToSort
		}

		let tableData = dataToSort
		const ascending = sortingColumn.ascending
		switch (sortingColumn.column) {
			case "Code":
				tableData.sort((a, b) => ascending ? a.name.localeCompare(b.name) : b.name?.localeCompare(a.name))
				break;
			case "Minutes":
				tableData.sort((a, b) => ascending ? a.minutes - b.minutes : b.minutes - a.minutes)
				break;
			case "views":
				tableData.sort((a, b) => ascending ? a.views - b.views : b.views - a.views)
				break;
			case "Downloads":
				tableData.sort((a, b) => ascending ? a.downloads - b.downloads : b.downloads - a.downloads)
				break;
			default:
				break;
		}
		return tableData
	}

	// Metrics
	const totalViews = aggregates.reduce((a, c) => a + (c.views || 0), 0)
	const mostDownloadedPortafolio = aggregates?.toSorted((a, b) => b.downloads - a.downloads)[0]
	const mostViewedPortafolio = aggregates?.toSorted((a, b) => b.views - a.views)[0]

	const dataDisplaying = hasSearchTerm ? filteredData : aggregates


	// table data
	const colWidths = ["auto", "150px", "150px", "150px"];
	const headers = [
		{
			text: (
				<div className='sorting-header' onClick={() => toggleSort("Code")} >
					Code
					{sortingColumn && sortingColumn.column === "Code" && sortingColumn.ascending === true ?
						<ChevronUp className="colour-text-primary" size={12} /> :
						<ChevronDown className={sortingColumn && sortingColumn.column === "Code" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
					}
				</div>
			)
		},
		{
			className: "text-align-right",
			text: (
				<Tooltip label="Minutes">
					<div className='sorting-header' onClick={() => toggleSort("Minutes")}>
						<Clock className="colour-text-primary" size={16} />
						{sortingColumn && sortingColumn.column === "Minutes" && sortingColumn.ascending === true ?
							<ChevronUp className="colour-text-primary" size={12} /> :
							<ChevronDown className={sortingColumn && sortingColumn.column === "Minutes" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
						}
					</div>
				</Tooltip>
			),
		},
		{
			className: "text-align-right",
			text: (
				<Tooltip label="Unique Portal Views">
					<div className='sorting-header' onClick={() => toggleSort("views")}>
						<Eye className="colour-text-primary" size={16} />
						{sortingColumn && sortingColumn.column === "views" && sortingColumn.ascending === true ?
							<ChevronUp className="colour-text-primary" size={12} /> :
							<ChevronDown className={sortingColumn && sortingColumn.column === "views" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
						}
					</div>
				</Tooltip>
			)
		},
		{
			className: "text-align-right",
			text: (
				<Tooltip label="Report Downloads">
					<div className='sorting-header' onClick={() => toggleSort("Downloads")}>
						<Download className='colour-text-positive-dark' size={16} />
						{sortingColumn && sortingColumn.column === "Downloads" && sortingColumn.ascending === true ?
							<ChevronUp className="colour-text-primary" size={12} /> :
							<ChevronDown className={sortingColumn && sortingColumn.column === "Downloads" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
						}
					</div>
				</Tooltip>
			)
		}
	]

	const dataMap = (data) => data.map((ticker, idx) => {
		return (
			<tr key={idx} className="cursor-pointer" onClick={() => history.push(`${ROUTE_PATHS[PAGES.ANALYTICS]}/portfolios/${ticker.number}`)}>
				<td>
					<TickerSectorItem
						imageClass='manager-logo-image-container'
						label={ticker.name}
						extensionLabel={ticker.manager}
						image={ticker?.logo}
					/>
				</td>
				<td><div className="text-align-right">{minutesIntoReadableFormat(ticker.minutes)}</div></td>
				<td><div className="text-align-right">{ticker.views}</div></td>
				<td><div className="text-align-right">{ticker.downloads}</div></td>
			</tr>
		)
	});

	const sortedDataToDisplay = sortTable(dataDisplaying)
	const dataToDisplay = !!sortedDataToDisplay ? dataMap(sortedDataToDisplay) : null

	const summaryExportHeaders = useMemo(() => [
		{ label: 'Metric', key: 'metric' },
		{ label: 'Value', key: 'value' },
	], []);
	const summaryExportData = loading ? [] : [
		{ metric: 'Total Views', value: totalViews },
		{ metric: 'Most Views', value: mostViewedPortafolio?.name || '' },
		{ metric: 'Most Downloads', value: mostDownloadedPortafolio?.name || '' },
	];
	const tableExportHeaders = useMemo(() => [
		{ label: 'Code', key: 'name' },
		{ label: 'Manager', key: 'manager' },
		{ label: 'Minutes', key: 'minutes' },
		{ label: 'Views', key: 'views' },
		{ label: 'Downloads', key: 'downloads' },
	], []);
	const tableExportData = (sortedDataToDisplay || []).map(p => ({
		name: p.name,
		manager: p.manager || '',
		minutes: p.minutes || 0,
		views: p.views || 0,
		downloads: p.downloads || 0,
	}));

	return (
		<Page noBanner>
			<PageContentBodyGrid rowGap="xl">
				<PageGridItem colSpan="12">
					<FlexWrapper fullWidth justify="space-between" style={{ paddingBottom: "1rem" }}>
						<FlexWrapper gap="xl">
							<FlexWrapper gap="xs" direction="column" align="flex-start" >
								<label className='total-metrics-label'>Total Views</label>
								{loading ? <LoadingIcon size={32} /> : <h2 className='total-metrics-value'>{totalViews}</h2>}
							</FlexWrapper>
							<Tooltip disabled={!mostViewedPortafolio || (mostViewedPortafolio && mostViewedPortafolio.views === 0)} label={mostViewedPortafolio && `${mostViewedPortafolio.views} views`}>
								<FlexWrapper gap="xs" direction="column" align="flex-start" >
									<label className='total-metrics-label'>Most Views</label>
									{loading ? <LoadingIcon size={32} /> : <HeaderTickerDisplay ticker={mostViewedPortafolio} property="views" />}
								</FlexWrapper>
							</Tooltip>
							<Tooltip disabled={!mostDownloadedPortafolio || (mostDownloadedPortafolio && mostDownloadedPortafolio.downloads === 0)} label={mostDownloadedPortafolio && `${mostDownloadedPortafolio.downloads} downloads`}>
								<FlexWrapper gap="xs" direction="column" align="flex-start" >
									<label className='total-metrics-label'>Most Downloads</label>
									{loading ? <LoadingIcon size={32} /> : <HeaderTickerDisplay ticker={mostDownloadedPortafolio} property="downloads" />}
								</FlexWrapper>
							</Tooltip>
						</FlexWrapper>
						<AnalyticsExportButton
							headers={summaryExportHeaders}
							data={summaryExportData}
							filename={analyticsExportFilenameDateless('portfolios-summary')}
							loading={loading}
						/>
					</FlexWrapper>
					<PageGridDivider />
					<PageGridItem style={{ paddingTop: "1.5rem" }}>
						<SearchTermFilter
							size="small"
							width="l"
							placeholder={`Search`}
							isClearable
							dataSet={aggregates}
							setFilteredData={setFilteredData}
							path={["name", "manager", "number"]}
							hasSearchTerm={(boolean) => setHasSearchTerm(boolean)}
						/>
					</PageGridItem>
					<FlexWrapper fullWidth justify="space-between" align="center">
						<div className="table-title">All Portfolios</div>
						<AnalyticsExportButton
							headers={tableExportHeaders}
							data={tableExportData}
							filename={analyticsExportFilenameDateless('portfolios-table')}
							loading={loading}
						/>
					</FlexWrapper>
					{loading ? <LoadingIcon centered size={48} /> :
						<Table
							minWidth={900}
							colWidths={colWidths}
							headers={headers}
							dataMap={dataToDisplay}
							noResults={{ message: "No tickers found" }}
							tableStyle={{ maxWidth: 1200 }}
						/>
					}
				</PageGridItem>
			</PageContentBodyGrid>
		</Page>
	);
}

export default AnalyticsPortfoliosList;